import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { AlertTriangle, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSenderConfigurations } from '../hooks/useSenders';
import { Sender } from '../types/sender.types';
import { compterDossiersAffectes } from '../utils/dossiers-affectes';

interface SenderDeleteModalProps {
  isOpen: boolean;
  onClose: () => void;
  sender: Sender | null;
  onConfirm: () => void;
  isLoading?: boolean;
}

export const SenderDeleteModal = ({
  isOpen,
  onClose,
  sender,
  onConfirm,
  isLoading = false,
}: SenderDeleteModalProps) => {
  const { t } = useTranslation();
  // Supprimer un expéditeur retire aussi ses affectations (cascade en base) :
  // le cabinet doit savoir combien de dossiers repartiront sans lui.
  const { data: configurations = [] } = useSenderConfigurations();
  const dossiersAffectes = sender
    ? compterDossiersAffectes(configurations, sender.uuid)
    : 0;

  if (!sender) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('senders.delete.title', 'Delete Sender')}
      size="lg"
    >
      <div className="space-y-6">
        {/* Warning Icon */}
        <div className="flex justify-center">
          <div className="bg-error-100 flex h-16 w-16 items-center justify-center rounded-full">
            <AlertTriangle className="text-error-600 h-8 w-8" />
          </div>
        </div>

        {/* Content */}
        <div className="text-center">
          <h3 className="text-lg font-semibold text-neutral-900">
            {t('senders.delete.confirmTitle', 'Delete this sender?')}
          </h3>
          <p className="mt-2 text-sm text-neutral-600">
            {t(
              'senders.delete.confirmDescription',
              'This action cannot be undone. This sender will be permanently deleted and cannot be recovered.'
            )}
          </p>
        </div>

        {/* Sender Info */}
        <div className="rounded-xl border border-neutral-200 bg-neutral-50 p-4">
          <div className="flex items-center gap-3">
            <div className="bg-primary-100 flex h-10 w-10 items-center justify-center rounded-xl">
              <Trash2 className="text-primary-600 h-5 w-5" />
            </div>
            <div className="min-w-0 flex-1">
              <h4 className="font-semibold text-neutral-900">{sender.name}</h4>
              <p className="text-sm text-neutral-600">{sender.email}</p>
            </div>
          </div>
        </div>

        {dossiersAffectes > 0 && (
          <p className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
            Cet expéditeur est affecté à {dossiersAffectes} dossier
            {dossiersAffectes > 1 ? 's' : ''}. Leurs envois repartiront avec
            l’expéditeur par défaut du cabinet.
          </p>
        )}

        {/* Actions */}
        <div className="flex gap-3">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isLoading}
            className="flex-1"
          >
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button
            variant="error"
            onClick={onConfirm}
            disabled={isLoading}
            className="flex-2"
          >
            {isLoading ? (
              <>
                <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                {t('senders.delete.deleting', 'Deleting...')}
              </>
            ) : (
              <>
                <Trash2 className="mr-2 h-4 w-4" />
                {t('senders.delete.confirm', 'Delete Sender')}
              </>
            )}
          </Button>
        </div>
      </div>
    </Modal>
  );
};
